import React, { Component } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import ReactHighcharts from '../CommonComponents/Highcharts';
import LoadingChart from '../CommonComponents/LoadingChart';
import { Text } from '../CommonComponents/OPL/Text/Text';
import { ErrorState } from '../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';

export default class MonteCarloChartView extends Component {
  componentDidMount() {
    const { clientId, setUpdateCacheData } = this.props;

    this.loadIfNecessary();
    setUpdateCacheData(clientId, true);
  }

  componentDidUpdate() {
    this.loadIfNecessary();
  }

  componentWillUnmount() {
    const { clientId, setUpdateCacheData, abortMcRequests } = this.props;
    setUpdateCacheData(clientId, false);
    abortMcRequests();
  }

  loadIfNecessary() {
    const {
      fpId,
      clientId,
      isPdf,
      requestedWith,
      fetchMcData,
      fetchPostMcData,
      stagedWhatIfs,
      FRAOption,
    } = this.props;

    if (fpId !== requestedWith.fpId || clientId !== requestedWith.clientId) {
      fetchMcData(fpId, clientId);
    } else if (!isPdf && !isEqual(stagedWhatIfs, requestedWith.stagedWhatIfs)) {
      fetchPostMcData(fpId, clientId, isPdf, stagedWhatIfs, FRAOption);
    }
  }

  render() {
    const {
      chartsData,
      loading,
      isPdf,
      trialsCount,
      percentageOfSuccess,
      monteCarloSuccessRange,
      error,
    } = this.props;

    if (error) {
      return (
        <section className={`MonteCarloChart${isPdf ? ' is-print' : ''}`}>
          <Text
            fontSize="h6"
            color="info.500"
            display="inline-block"
            width="50%"
            style={{ lineHeight: '80px' }}
          >
            Monte Carlo
          </Text>

          <ErrorState message={error} />
        </section>
      );
    }

    const { chartData, pieChartData } = chartsData;

    let Content = null;
    let PieChartContent = null;

    if (chartData && pieChartData) {
      Content = (
        <ReactHighcharts
          autoResize
          container="MonteCarloSplineChartContainer"
          options={chartData}
        />
      );
      PieChartContent = (
        <ReactHighcharts
          autoResize
          container="MonteCarloPieChartContainer"
          options={pieChartData}
        />
      );
    }

    return (
      <section className={`MonteCarloChart${isPdf ? ' is-print' : ''}`}>
        <Text
          fontSize="h6"
          color="info.500"
          display="inline-block"
          width="50%"
          style={{ lineHeight: '80px' }}
        >
          Monte Carlo
        </Text>
        <ul className="legend">
          <li>
            <i />
            Successful Trials
          </li>
          <li>
            <i className="purple" />
            Unsuccessful Trials
          </li>
        </ul>
        <div className="top_bar">
          <div className="probability_chart">
            <div className="probability_chart_container">{PieChartContent}</div>
            <div className="chart_label">
              <div className="title">Probability of Success</div>
              <div className="target">Target: {monteCarloSuccessRange}-99%</div>
            </div>
          </div>
          <div className="explanation">
            <div className="inner_container">
              We ran <span>{trialsCount}</span> trials to simulate a range of possible outcomes.{' '}
              <b className="js_pos">{percentageOfSuccess}</b> of the time, you were fully funded for
              your retirement.
            </div>
          </div>
        </div>
        <div className="chart_container">
          {loading && <LoadingChart />}
          {Content}
        </div>
        <Text my="16" color="neutral.600">
          Monte Carlo is a process that is used to conduct a statistical analysis of a client’s
          portfolio to determine the probability of the client achieving their retirement goal. The
          investment returns for each year are calculated from the return of each asset class for
          that year and the asset allocation of each account. Asset class returns are generated from
          a random distribution. Each asset class is linked to an index, or a set of indices, with
          an assumed mean return and standard deviation. Using a correlation matrix between all the
          asset classes, and assuming a normal distribution of returns, we generate random returns
          for each year of the simulation. We run <span>{trialsCount}</span> trials each time using
          a different set of asset class returns taken from the distribution. At the end of the
          simulation, we calculate the percentage of trials in which the client was able to cover
          all of their expenses until the end of their retirement. This is the probability of
          success.
        </Text>

        <Text my="16" color="neutral.600">
          IMPORTANT: The projections or other information generated by this Monte Carlo simulation
          regarding the likelihood of various investment outcomes are hypothetical in nature, do not
          reflect actual investment results and are not guarantees of future results. There is no
          guarantee that the results generated by this simulation will be achieved or sustained, and
          results may vary with each use. Please note: This analysis must be reviewed in conjunction
          with the limitations and conditions described in the Disclosure section of this
          presentation. Projections are based on assumptions provided by you and your
          advisor/representative, and are not guaranteed. Actual results will vary, perhaps to a
          significant degree. The projected reports are hypothetical in nature and for illustrative
          purposes only.
        </Text>
      </section>
    );
  }
}

MonteCarloChartView.defaultProps = {
  isPdf: false,
  error: null,
};

MonteCarloChartView.propTypes = {
  clientId: PropTypes.string.isRequired,
  fpId: PropTypes.string.isRequired,
  chartsData: PropTypes.shape({
    chartData: PropTypes.shape({}),
    pieChartData: PropTypes.shape({}),
  }).isRequired,
  requestedWith: PropTypes.shape({
    IsPdf: PropTypes.bool,
    clientId: PropTypes.string,
    fpId: PropTypes.string,
    stagedWhatIfs: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  isPdf: PropTypes.bool,
  trialsCount: PropTypes.number.isRequired,
  monteCarloSuccessRange: PropTypes.number.isRequired,
  percentageOfSuccess: PropTypes.string.isRequired,
  fetchMcData: PropTypes.func.isRequired,
  fetchPostMcData: PropTypes.func.isRequired,
  setUpdateCacheData: PropTypes.func.isRequired,
  abortMcRequests: PropTypes.func.isRequired,
  stagedWhatIfs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  FRAOption: PropTypes.shape({
    UseFraPrincipal: PropTypes.bool,
    UseFraSpouse: PropTypes.bool,
    SocialSecurityStartAgePrincipal: PropTypes.number,
    SocialSecurityStartAgeSpouse: PropTypes.number,
  }).isRequired,
};
